/**
 * Assembles the Claim Thread (Composition §12) from payloads already loaded for a stream.
 *
 * Pure by design — it fetches nothing, which is what makes it testable and what lets the thread
 * open instantly from any workspace. The join key is `claim_id`, the same value in the signal, the
 * prenota, the verification and the note.
 *
 * Stations the claim never reached are returned with `reached: false` rather than omitted: the gap
 * is the finding. A claim that went from normalized straight into a draft without ever being
 * verified is exactly what an editor needs to see.
 */

import type {
  EditorialClaimAssessment,
  EditorialCriteriaMatch,
  EditorialNormalizedClaim,
  EditorialOutput,
  EditorialPrenota,
  EditorialSignal,
  EditorialVerification,
} from 'src/types/editorial';

export const THREAD_STAGES = [
  'detected',
  'normalized',
  'evaluated',
  'in_story',
  'verified',
  'drafted',
  'reviewed',
  'published',
] as const;

export type ThreadStage = (typeof THREAD_STAGES)[number];

export const STAGE_LABEL = {
  detected: 'Detectado',
  normalized: 'Normalizado',
  evaluated: 'Evaluado',
  in_story: 'En historia',
  verified: 'Verificado',
  drafted: 'Redactado',
  reviewed: 'Revisado',
  published: 'Publicado',
} satisfies Record<ThreadStage, string>;

/** Steps 10 and 11 of the process do not exist yet. Art Direction §7: `not-built` is honest, and
 *  pretending otherwise makes the whole thread untrustworthy. */
export const NOT_BUILT_STAGES: ReadonlySet<ThreadStage> = new Set<ThreadStage>(['published']);

export type ThreadStation =
  | { stage: 'detected'; reached: true; excerpt: string; speaker: string; timestamp: string; chunkIds: string[] }
  | { stage: 'normalized'; reached: true; claim: EditorialNormalizedClaim }
  | { stage: 'evaluated'; reached: true; matches: EditorialCriteriaMatch[] }
  | { stage: 'in_story'; reached: true; stories: Array<{ storyId: string; headline: string; viaStoryLevel: boolean }> }
  | { stage: 'verified'; reached: true; assessment: EditorialClaimAssessment; storyKey: string }
  | { stage: 'drafted'; reached: true; usages: Array<{ transformationId: string; profileId: string; blockIndex: number | null; blockText: string }> }
  | { stage: 'reviewed'; reached: true; reviewCount: number }
  | {
      stage: ThreadStage;
      reached: false;
      absence: 'not_reached' | 'skipped' | 'not_built' | 'incompatible_ids';
      note: string;
    };

export type ClaimThread = {
  claimId: string;
  stations: ThreadStation[];
  /** `contradicts` / `supports` links to other claims — the seed of a controversy. */
  relationships: Array<{ claimId: string; relationship: string }>;
};

export type ThreadSources = {
  signals: EditorialSignal[];
  criteriaMatches: EditorialCriteriaMatch[];
  prenotas: EditorialPrenota[];
  verifications: EditorialVerification[];
  outputs: EditorialOutput[];
};

/**
 * Streams processed before the claim thread was closed at step 4 mint a *different* `claim_id` in
 * the prenota and the note than the one normalization assigned — `clm_`-prefixed on one side, bare
 * hash on the other. The two id spaces then never intersect.
 *
 * Detecting that matters because the alternative is worse than saying nothing: "no draft uses this
 * claim" reads as an editorial decision, when the truth is that the link was never recordable. Art
 * Direction §7 — skipped, failed and incompatible are different facts about the world.
 */
export function hasIncompatibleClaimIds(sources: ThreadSources): boolean {
  const signalIds = new Set<string>();
  for (const signal of sources.signals) {
    for (const claim of signal.payload.normalized_claims) signalIds.add(claim.claim_id);
  }

  const downstreamIds = new Set<string>();
  for (const prenota of sources.prenotas) {
    for (const id of prenota.payload.source_claim_ids ?? []) downstreamIds.add(id);
    for (const claim of prenota.payload.claims) if (claim.claim_id) downstreamIds.add(claim.claim_id);
  }
  for (const output of sources.outputs) {
    for (const id of output.payload.traceability?.used_claim_ids ?? []) downstreamIds.add(id);
  }

  // Only a claim: both sides produced ids and none of them match.
  if (signalIds.size === 0 || downstreamIds.size === 0) return false;
  for (const id of downstreamIds) if (signalIds.has(id)) return false;
  return true;
}

const INCOMPATIBLE_NOTE =
  'Esta transmisión se procesó antes de que el hilo del claim se cerrara en el paso 4: la prenota y la nota ' +
  'usan un espacio de identificadores distinto al de la normalización, así que el vínculo no quedó registrado. ' +
  'No significa que el claim no se haya usado — significa que no se puede saber desde aquí.';

export function buildClaimThread(claimId: string, sources: ThreadSources): ClaimThread | null {
  const claim = findNormalizedClaim(claimId, sources.signals);
  if (!claim) return null;

  const incompatible = hasIncompatibleClaimIds(sources);

  return {
    claimId,
    relationships: (claim.relationships ?? []).map(link => ({
      claimId: link.claim_id,
      relationship: link.relationship,
    })),
    stations: [
      detectedStation(claim),
      { stage: 'normalized', reached: true, claim },
      evaluatedStation(claim, sources.criteriaMatches),
      inStoryStation(claimId, sources.prenotas, incompatible),
      verifiedStation(claimId, sources.verifications),
      draftedStation(claimId, sources.outputs, incompatible),
      // Review records reference a draft, not a claim; the count is resolved by the workspace that
      // already loaded them rather than re-derived here.
      { stage: 'reviewed', reached: false, absence: 'not_reached', note: 'Sin revisión editorial registrada para las notas que usan este claim.' },
      { stage: 'published', reached: false, absence: 'not_built', note: 'El paso de publicación aún no existe en el pipeline.' },
    ],
  };
}

export function findNormalizedClaim(claimId: string, signals: EditorialSignal[]): EditorialNormalizedClaim | null {
  for (const signal of signals) {
    const found = signal.payload.normalized_claims.find(claim => claim.claim_id === claimId);
    if (found) return found;
  }
  return null;
}

/** Indexes every normalized claim in the stream once, so a workspace rendering hundreds of rows
 *  does not rescan the signal list per row. */
export function indexNormalizedClaims(signals: EditorialSignal[]): Map<string, EditorialNormalizedClaim> {
  const index = new Map<string, EditorialNormalizedClaim>();
  for (const signal of signals) {
    for (const claim of signal.payload.normalized_claims) {
      if (!index.has(claim.claim_id)) index.set(claim.claim_id, claim);
    }
  }
  return index;
}

function detectedStation(claim: EditorialNormalizedClaim): ThreadStation {
  return {
    stage: 'detected',
    reached: true,
    excerpt: claim.evidence.excerpt,
    speaker: claim.speaker.name,
    timestamp: claim.evidence.timestamps[0] ?? '',
    chunkIds: claim.evidence.chunk_ids,
  };
}

function evaluatedStation(claim: EditorialNormalizedClaim, matches: EditorialCriteriaMatch[]): ThreadStation {
  const chunkIds = new Set(claim.evidence.chunk_ids);
  const relevant = matches.filter(match => chunkIds.has(match.chunkId));

  if (relevant.length === 0) {
    return {
      stage: 'evaluated',
      reached: false,
      absence: 'not_reached',
      note: 'Ningún criterio editorial coincidió con el bloque de este claim.',
    };
  }
  return { stage: 'evaluated', reached: true, matches: relevant };
}

function inStoryStation(claimId: string, prenotas: EditorialPrenota[], incompatible: boolean): ThreadStation {
  const stories = prenotas.flatMap(prenota => {
    const atStoryLevel = (prenota.payload.source_claim_ids ?? []).includes(claimId);
    const atClaimLevel = prenota.payload.claims.some(claim => claim.claim_id === claimId);
    if (!atStoryLevel && !atClaimLevel) return [];
    return [
      {
        storyId: prenota.storyId,
        headline: prenota.payload.headline,
        // Design System §7: per-claim inheritance is a token-overlap match and can miss on a
        // paraphrased claim. When only the story level has it, say so rather than imply a drop.
        viaStoryLevel: atStoryLevel && !atClaimLevel,
      },
    ];
  });

  if (stories.length === 0) {
    return incompatible
      ? { stage: 'in_story', reached: false, absence: 'incompatible_ids', note: INCOMPATIBLE_NOTE }
      : {
          stage: 'in_story',
          reached: false,
          absence: 'not_reached',
          note: 'Este claim no entró en ninguna prenota.',
        };
  }
  return { stage: 'in_story', reached: true, stories };
}

function verifiedStation(claimId: string, verifications: EditorialVerification[]): ThreadStation {
  for (const verification of verifications) {
    const assessment = verification.payload.claim_assessments.find(item => item.claim_id === claimId);
    if (assessment) {
      return { stage: 'verified', reached: true, assessment, storyKey: verification.storyKey };
    }
  }
  return {
    stage: 'verified',
    reached: false,
    absence: verifications.length > 0 ? 'skipped' : 'not_reached',
    note:
      verifications.length > 0
        ? 'Se verificaron otras historias de esta transmisión, pero este claim no fue evaluado.'
        : 'No se ejecutó verificación en esta transmisión.',
  };
}

function draftedStation(claimId: string, outputs: EditorialOutput[], incompatible: boolean): ThreadStation {
  const usages = outputs.flatMap(output => {
    const blocks = output.payload.content?.body_blocks ?? [];
    const blockIndex = blocks.findIndex(block => (block.claim_ids ?? []).includes(claimId));
    const usedAtAll = blockIndex >= 0 || (output.payload.traceability?.used_claim_ids ?? []).includes(claimId);
    if (!usedAtAll) return [];
    return [
      {
        transformationId: output.transformationId,
        profileId: output.profileId,
        blockIndex: blockIndex >= 0 ? blockIndex : null,
        blockText: blockIndex >= 0 ? (blocks[blockIndex]?.text ?? '') : '',
      },
    ];
  });

  if (usages.length === 0) {
    return incompatible
      ? { stage: 'drafted', reached: false, absence: 'incompatible_ids', note: INCOMPATIBLE_NOTE }
      : {
          stage: 'drafted',
          reached: false,
          absence: 'not_reached',
          note: 'Ninguna nota generada usa este claim.',
        };
  }
  return { stage: 'drafted', reached: true, usages };
}

/** Why the drafter left a claim out — editorial information, not an error. */
export function findOmissionReasons(claimId: string, outputs: EditorialOutput[]): Array<{ profileId: string; reason: string }> {
  return outputs.flatMap(output => {
    const omission = (output.payload.traceability?.omitted_claims ?? []).find(item => item.claim_id === claimId);
    return omission ? [{ profileId: output.profileId, reason: omission.reason }] : [];
  });
}
